import { Link, useNavigate } from "react-router-dom";
import { Play, Pause, Lock } from "lucide-react";
import { Reveal } from "../Motion";
import JoinForm from "../JoinForm";
import { usePlayer, PLAYABLE } from "../../context/PlayerContext";

/* ---------- 3. FEATURED MUSIC — NOW IN MOTION ---------- */
export function NowInMotion({ heading, featured, releases }) {
  const navigate = useNavigate();
  const { playTrack, isCurrent, playing, toggle } = usePlayer();
  if (!featured) return null;
  const tracks = (featured.trackList || []).slice(0, 4);
  const others = (releases || []).filter((r) => r.slug !== featured.slug).slice(0, 2);

  return (
    <section className="relative py-24 md:py-36 bg-[#070707]" data-testid="now-in-motion">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-12">{heading || "NOW IN MOTION"}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <Reveal className="md:col-span-4" y={50}>
            <button onClick={() => navigate(`/music/${featured.slug}`)} data-testid="nim-artwork"
              className="group relative block w-full overflow-hidden rounded-[14px]">
              <img src={featured.artwork} alt={featured.title}
                className="w-full aspect-square object-cover transition-transform duration-[1.4s] group-hover:scale-[1.04]" />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10 rounded-[14px]" />
            </button>
            <p className="font-mono text-[10px] tracking-[0.3em] text-white/40 mt-5">
              {featured.releaseType?.toUpperCase()} · {featured.status}
            </p>
            <p className="font-serif text-2xl text-white mt-2">
              {featured.titleParts ? `${featured.titleParts.chapter} — ${featured.titleParts.name}` : featured.title}
            </p>
          </Reveal>
          <div className="md:col-span-8">
            <ul>
              {tracks.map((t, i) => {
                const playable = PLAYABLE.includes(t.availability);
                const cur = isCurrent(featured.slug, t.trackNumber);
                return (
                  <Reveal key={t.trackNumber} delay={i * 0.06}>
                    <li className="flex items-center gap-5 py-5 border-t border-white/10" data-testid={`nim-track-${t.trackNumber}`}>
                      <button
                        onClick={() => (cur ? toggle() : playTrack(t, { releaseSlug: featured.slug, releaseTitle: featured.title, artwork: featured.artwork, dimension: featured.dimension, queue: tracks }))}
                        data-testid={`nim-track-${t.trackNumber}-play`}
                        aria-label={playable ? `Play ${t.title}` : `${t.title} — ${t.lockLabel}`}
                        className={`w-10 h-10 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                          cur ? "border-gold bg-gold text-black"
                            : playable ? "border-gold/50 text-gold hover:bg-gold hover:text-black"
                            : "border-white/15 text-white/30 hover:border-white/40"}`}
                      >
                        {!playable ? <Lock size={12} /> : cur && playing ? <Pause size={13} /> : <Play size={13} className="ml-0.5" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="font-serif text-xl md:text-2xl font-light text-white truncate">{t.title}</p>
                        {!playable && <p className="font-mono text-[8px] tracking-[0.3em] text-white/30 mt-1">{t.lockLabel}</p>}
                      </div>
                      {t.slug && (
                        <Link to={`/track/${t.slug}`} data-testid={`nim-track-${t.trackNumber}-page`}
                          className="font-mono text-[9px] tracking-[0.25em] text-white/40 hover:text-gold transition-colors hidden sm:block">
                          LYRICS &amp; MEANING →
                        </Link>
                      )}
                      <span className="font-mono text-[11px] text-white/40 w-10 text-right">{t.duration}</span>
                    </li>
                  </Reveal>
                );
              })}
            </ul>
            {others.map((r) => (
              <Reveal key={r.slug}>
                <button onClick={() => navigate(`/music/${r.slug}`)} data-testid={`nim-release-${r.slug}`}
                  className="group w-full flex items-center justify-between py-5 border-t border-white/10 text-left hover:px-3 transition-all duration-300">
                  <div>
                    <span className="font-serif text-xl md:text-2xl font-light text-white group-hover:text-gold transition-colors">{r.title}</span>
                    <p className="font-mono text-[9px] tracking-[0.3em] text-white/35 mt-1.5">{r.releaseType?.toUpperCase()} · {r.year}</p>
                  </div>
                  <span className="font-mono text-gold">→</span>
                </button>
              </Reveal>
            ))}
            <Reveal>
              <div className="border-t border-white/10 pt-7 flex flex-wrap gap-8">
                <Link to={`/music/${featured.slug}`} data-testid="nim-explore-release"
                  className="font-mono text-[11px] tracking-[0.25em] text-gold hover:text-white transition-colors">EXPLORE THE RELEASE →</Link>
                <Link to="/music" data-testid="nim-full-catalog"
                  className="font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors">VIEW THE FULL CATALOG →</Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------- 6. SUPPORTERS WALL PREVIEW ---------- */
export function SupportersPreview({ supporters }) {
  const list = (supporters || []).slice(0, 8);
  return (
    <section className="relative py-24 md:py-36 bg-[#070707]" data-testid="supporters-preview">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">THE SUPPORTERS</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light text-white max-w-3xl">
            BECOME PART OF THE JOURNEY.
          </h2>
        </Reveal>
        {list.length > 0 ? (
          <div className="flex flex-wrap gap-3 mt-12">
            {list.map((s, i) => (
              <Reveal key={s.id || i} delay={i * 0.05}>
                <div className="border border-white/10 px-5 py-3 flex items-center gap-3" data-testid={`supporter-chip-${i}`}>
                  <span className="font-serif text-lg text-white/85">{s.displayName}</span>
                  {s.designation && (
                    <span className="font-mono text-[8px] tracking-[0.25em] text-gold/80 border border-gold/25 px-2 py-0.5">
                      {s.designation.toUpperCase()}
                    </span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal>
            <p className="text-white/40 mt-10 max-w-md" data-testid="supporters-empty">
              The wall awaits its first names. Those who move the music forward are recognized here.
            </p>
          </Reveal>
        )}
        <Reveal delay={0.1}>
          <div className="flex flex-wrap gap-5 mt-12">
            <Link to="/store" data-testid="supporters-cta-support"
              className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:bg-white transition-colors">SUPPORT THE MUSIC</Link>
            <Link to="/inner-circle" data-testid="supporters-cta-circle"
              className="border border-white/30 text-white font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors">JOIN THE INNER CIRCLE</Link>
            <Link to="/supporters" data-testid="supporters-cta-view"
              className="font-mono text-[11px] tracking-[0.25em] text-white/50 hover:text-gold transition-colors self-center">VIEW SUPPORTERS →</Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 7. MULTIDIMENSIONAL COMPLETE SET ---------- */
export function CompleteSetSection({ set }) {
  const navigate = useNavigate();
  if (!set) return null;
  return (
    <section className="relative py-24 md:py-36 bg-[#050505] overflow-hidden" data-testid="complete-set-section">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.05),transparent_65%)]" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="border border-gold/20 bg-[#0a0a08] p-10 md:p-16">
            <p className="font-mono text-[10px] tracking-[0.45em] text-gold mb-6">THE COLLECTOR RELEASE</p>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl font-light text-white leading-[1.05]">
              THE COMPLETE<br />MULTI<wbr />DIMENSIONAL COLLECTION
            </h2>
            <div className="flex flex-wrap gap-2.5 mt-8">
              {(set.includedDimensions || []).map((n) => (
                <span key={n} className="font-mono text-[9px] tracking-[0.3em] text-white/60 border border-white/15 px-4 py-2">DIMENSION {n}</span>
              ))}
            </div>
            <p className="text-white/55 leading-relaxed mt-8 max-w-xl">{set.description}</p>
            <p className="font-mono text-[10px] tracking-[0.3em] text-gold/80 mt-6" data-testid="complete-set-dv-note">
              OWNING ALL FOUR PHYSICAL DIMENSIONS ESTABLISHES DIMENSION V ELIGIBILITY.
            </p>
            <div className="flex flex-wrap items-center gap-6 mt-10">
              <button onClick={() => navigate("/complete-set")} data-testid="complete-set-cta"
                className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors">
                EXPLORE THE COMPLETE SET
              </button>
              <span className="font-mono text-[9px] tracking-[0.35em] text-white/35" data-testid="complete-set-status">
                {set.available ? (set.preorder ? "PREORDER OPEN" : "AVAILABLE NOW") : set.releaseDateInfo || "TO BE ANNOUNCED"}
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 8. DIMENSION V TEASER ---------- */
export function DimensionVTeaser({ user }) {
  const entitled = user && user.dimensionV === "active";
  return (
    <section className="relative py-24 md:py-32 bg-[#030303] overflow-hidden" data-testid="dimension-v-teaser">
      <div className="signal-static" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 text-center">
        <Reveal>
          <span className="font-serif text-7xl md:text-8xl text-white/15">05</span>
          <p className="font-mono text-xs tracking-[0.45em] text-red-500/80 mt-5">
            {entitled ? "DIMENSION V — ACCESS VERIFIED" : "DIMENSION V — SIGNAL DETECTED"}
          </p>
          <p className="text-white/45 max-w-md mx-auto mt-6 text-sm leading-relaxed">
            Beyond the four known Dimensions. Access is reserved for verified owners of all four
            physical Dimensions — collected together through the Complete Set or separately.
          </p>
          <div className="flex flex-wrap justify-center gap-5 mt-10">
            {entitled ? (
              <Link to="/dimension-v" data-testid="dv-teaser-enter"
                className="bg-gold text-black font-mono text-[11px] tracking-[0.25em] px-9 py-4 hover:bg-white transition-colors">ENTER DIMENSION V</Link>
            ) : (
              <>
                <Link to="/complete-set" data-testid="dv-teaser-learn"
                  className="border border-white/25 text-white/80 font-mono text-[11px] tracking-[0.25em] px-8 py-4 hover:border-gold hover:text-gold transition-colors">LEARN HOW TO UNLOCK</Link>
                <Link to="/dimension-v/access" data-testid="dv-teaser-redeem"
                  className="font-mono text-[11px] tracking-[0.25em] text-white/40 hover:text-white transition-colors self-center">REDEEM ACCESS KEY →</Link>
              </>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------- 10. NEWSLETTER ---------- */
export function NewsletterSection({ heading, copy }) {
  return (
    <section className="relative py-24 md:py-36 bg-[#070707]" data-testid="newsletter-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-14 items-center">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.4em] text-gold mb-6">THE SIGNAL</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light text-white">{heading || "JOIN THE MAILING LIST"}</h2>
          {copy && (
            <p className="text-white/50 mt-5 max-w-md leading-relaxed">
              {copy}
            </p>
          )}
        </Reveal>
        <Reveal delay={0.1}>
          <JoinForm />
        </Reveal>
      </div>
    </section>
  );
}
